import {
  Button,
  Card,
  IconButton,
  TableBody,
  TableCell,
  TableRow
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import { BannerDetails_banner_coupon } from "@saleor/banners/types/BannerDetails";
import CardTitle from "@saleor/components/CardTitle/CardTitle";
import ResponsiveTable from "@saleor/components/ResponsiveTable/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableHead from "@saleor/components/TableHead";
import { maybe } from "@saleor/misc";
import moment from "moment";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface BannerCouponProps {
  coupon: BannerDetails_banner_coupon;
  onCouponAssign?: () => void;
  onCouponUnassign?: (id: string) => void;
  disabled?: boolean;
  onRowClick?: (id: string) => void;
}

const useStyles = makeStyles(
  theme => ({
    buttonDisabled: {
      "& > span": {
        color: "#616161"
      }
    },
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 76 + theme.spacing(0.5)
    },
    colEmpty: {
      width: "1px"
    },
    colName: {
      paddingLeft: 0,
      width: 230
    },
    colNameLabel: {},
    colPrice: {
      textAlign: "center",
      width: 100
    },
    colPublished: {
      width: 150
    },
    colSKU: {
      "@media (min-width: 769px)": {
        wordBreak: "break-all"
      },
      width: 150
    },
    colType: {
      width: 150
    },
    table: {
      tableLayout: "fixed"
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "BannerCoupons" }
);

const numberOfColumns = 5;

const BannerCoupons: React.FC<BannerCouponProps> = props => {
  const { coupon, onCouponAssign, onCouponUnassign, disabled } = props;
  const classes = useStyles(props);

  const intl = useIntl();

  function showCouponTable() {
    return (
      <ResponsiveTable className={classes.table}>
        <colgroup>
          <col className={classes.colName} />
          <col className={classes.colType} />
          <col className={classes.colType} />
          <col className={classes.colType} />
          <col className={classes.colActions} />
        </colgroup>
        <TableHead
          colSpan={numberOfColumns}
          disabled={false}
          hideCheckBox
          items={coupon ? [coupon] : []}
          toolbar={[toolbar]}
        >
          <TableCell className={classes.colName}>
            <span className={classes.colNameLabel}>
              <FormattedMessage defaultMessage="Coupon Title" />
            </span>
          </TableCell>
          <TableCell className={classes.colType}>
            <FormattedMessage defaultMessage="Status" />
          </TableCell>
          <TableCell className={classes.colType}>
            <FormattedMessage defaultMessage="Claiming Start Date" />
          </TableCell>
          <TableCell className={classes.colType}>
            <FormattedMessage defaultMessage="Claiming End Date" />
          </TableCell>
          <TableCell className={classes.colActions} />
        </TableHead>
        <TableBody>
          {coupon ? (
            <TableRow
              hover={!!coupon}
              key={coupon ? coupon.id : "skeleton"}
              className={classes.tableRow}
            >
              <TableCell className={classes.colName} id="couponName">
                {maybe<React.ReactNode>(() => coupon.name, <Skeleton />)}
              </TableCell>
              <TableCell className={classes.colType} id="couponStatus">
                {maybe<React.ReactNode>(() => coupon.status, <Skeleton />)}
              </TableCell>
              <TableCell
                className={classes.colType}
                id="couponClaimingStartDate"
              >
                {moment(coupon?.startDate).format("MMMM D, YYYY HH:mm:ssA")}
              </TableCell>
              <TableCell className={classes.colType} id="couponClaimingEndDate">
                {moment(coupon?.endDate).format("MMMM D, YYYY HH:mm:ssA")}
              </TableCell>
              <TableCell className={classes.colActions} id="couponDelete">
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    onCouponUnassign(coupon.id);
                  }}
                >
                  <DeleteIcon color="primary" />
                </IconButton>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No coupons assigned yet" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </ResponsiveTable>
    );
  }

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Coupons",
          description: "section header"
        })}
        toolbar={
          <Button
            id="assignCouponBtn"
            color="primary"
            disabled={Boolean(coupon) || disabled}
            className={!!coupon || disabled ? classes.buttonDisabled : null}
            onClick={onCouponAssign}
          >
            <FormattedMessage
              defaultMessage="Assign Coupon"
              description="button"
            />
          </Button>
        }
      />
      {showCouponTable()}
    </Card>
  );
};
BannerCoupons.displayName = "BannerCoupons";
export default BannerCoupons;
